import { Badge, Button, Collapse, Divider, Group, Paper, Stack, Text } from "@mantine/core";
import { type MouseEvent, useCallback, useState } from "react";
import { formatDate } from "@/utils/date";
import { normalizeGuiaNotas } from "./rubrica-editor-utils";

type RubricaResumoProperties = {
  rubrica: TRubricaIdeias;
  titulo?: string;
};

/**
 *
 * @param root0
 * @param root0.rubrica
 * @param root0.titulo
 */
function RubricaResumo({ rubrica, titulo }: RubricaResumoProperties): JSX.Element {
  const [criteriosOpened, setCriteriosOpened] = useState(false);
  const [detalheIndex, setDetalheIndex] = useState<number | null>(null);
  const handleToggleCriterios = useCallback(() => {
    setCriteriosOpened((opened) => !opened);
  }, []);
  const handleToggleDetails = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    const indexValue = Number(event.currentTarget.dataset.index);

    if (Number.isNaN(indexValue)) {
      return;
    }

    setDetalheIndex((current) => (current === indexValue ? null : indexValue));
  }, []);

  return (
    <Paper withBorder p="md">
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <Group gap="xs">
              {titulo && <Badge variant="light">{titulo}</Badge>}
              <Text fw={600}>{rubrica.version}</Text>
            </Group>
            <Text size="sm" c="dimmed">
              {rubrica.descricao || "Sem descricao"}
            </Text>
          </Stack>
          <Text size="xs" c="dimmed">
            {formatDate(rubrica.createdAt)}
          </Text>
        </Group>

        <Group justify="space-between" align="center">
          <Text size="xs" c="dimmed">
            {rubrica.criterios.length} criterios
          </Text>
          <Button variant="transparent" size="xs" onClick={handleToggleCriterios}>
            {criteriosOpened ? "Ocultar critérios" : "Ver critérios"}
          </Button>
        </Group>

        <Collapse in={criteriosOpened}>
          <Stack gap="xs">
            {rubrica.criterios.map((criterio, index) => {
              const detailsOpen = detalheIndex === index;
              const guiaNotasEntries = normalizeGuiaNotas(criterio.guiaNotas || {});

              return (
                <Paper key={`${criterio.id}-${index}`} withBorder p="sm">
                  <Stack gap="xs">
                    <Group justify="space-between" align="flex-start">
                      <Stack gap={2} style={{ flex: 1 }}>
                        <Text fw={600} size="sm">
                          {criterio.nome || "Criterio sem nome"}
                        </Text>
                        <Text size="xs" c="dimmed">
                          ID: {criterio.id || "-"}
                        </Text>
                      </Stack>
                      <Group gap="xs" wrap="wrap">
                        <Badge variant="light">Peso {criterio.peso}</Badge>
                        <Badge variant="light">
                          Escala {criterio.escalaMin}-{criterio.escalaMax}
                        </Badge>
                        <Badge color={criterio.obrigatorio ? "green" : "gray"} variant="light">
                          {criterio.obrigatorio ? "Obrigatorio" : "Opcional"}
                        </Badge>
                      </Group>
                      <Button size="xs" variant="subtle" data-index={index} onClick={handleToggleDetails}>
                        {detailsOpen ? "Ocultar" : "Detalhes"}
                      </Button>
                    </Group>

                    <Collapse in={detailsOpen}>
                      <Divider my="xs" />
                      <Stack gap={6}>
                        <Stack gap={2}>
                          <Text size="xs" c="dimmed">
                            Descricao
                          </Text>
                          <Text size="sm">{criterio.descricao || "Sem descricao"}</Text>
                        </Stack>
                        <Stack gap={2}>
                          <Text size="xs" c="dimmed">
                            Guia de notas
                          </Text>
                          {guiaNotasEntries.length === 0 ? (
                            <Text size="sm" c="dimmed">
                              Sem guia de notas
                            </Text>
                          ) : (
                            <Stack gap={4}>
                              {guiaNotasEntries.map((entry) => (
                                <Group key={`${criterio.id}-${entry.nota}`} gap="xs">
                                  <Badge size="xs" variant="outline">
                                    {entry.nota}
                                  </Badge>
                                  <Text size="xs">{entry.descricao || "-"}</Text>
                                </Group>
                              ))}
                            </Stack>
                          )}
                        </Stack>
                      </Stack>
                    </Collapse>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
}

export default RubricaResumo;
